import React from 'react';
import {
  Button,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  DismissRegular,
  DocumentRegular,
} from '@fluentui/react-icons';
import DocumentViewer from './DocumentViewer';
import { DocumentInfo } from '../models/types';
import { ExtractedLink } from '../utils/linkExtractor';

interface GradeData {
  user_question: string;
  score: number;
  reasoning: string;
  timestamp: number;
  evaluator?: string;
}

interface DocumentPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  authorizationId: string;
  selectedDocument: DocumentInfo | null;
  onDocumentSelect: (document: DocumentInfo | null) => void;
  selectedDocuments: string[];
  onSelectedDocumentsChange: (documentGuids: string[]) => void;
  documents: DocumentInfo[];
  documentsLoading?: boolean;
  documentsError?: string | null;
  conversationLinks: ExtractedLink[];
  gradeData?: GradeData[];
  panelWidth: number;
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
  className?: string;
  classes: Record<string, string>;
  uiMode?: string;
}

const DocumentPanel: React.FC<DocumentPanelProps> = ({
  isOpen,
  onToggle,
  onClose,
  authorizationId,
  selectedDocument,
  onDocumentSelect,
  selectedDocuments,
  onSelectedDocumentsChange,
  documents,
  documentsLoading = false,
  documentsError = null,
  conversationLinks,
  gradeData = [],
  panelWidth,
  isResizing,
  handleResizeStart,
  className,
  classes,
  uiMode = 'authorization',
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className={className || classes.documentPanel}
      style={{ width: `${panelWidth}px` }}
    >
      {/* Resize Handle */}
      <div 
        className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeStart}
        title="Drag to resize panel"
      />
      
      <Button
        className={classes.panelToggleButton}
        appearance="secondary"
        icon={<ChevronLeftRegular />}
        onClick={onToggle}
        title={isOpen ? 'Close document panel' : 'Open document panel'}
      />
      
      <div className={classes.documentPanelHeader}>
        <Text size={400} weight="semibold">
          Documents
        </Text>
        <Button
          appearance="subtle"
          icon={<DismissRegular />}
          onClick={onClose}
          title="Close panel"
          size="small"
        />
      </div>
      
      <div className={classes.documentPanelContent} data-document-panel="true">
        {authorizationId?.trim() || conversationLinks.length > 0 ? (
          <>
            {/* Document Viewer */}
            <div className={classes.documentViewerContainer}>
              <DocumentViewer
                key={`doc-viewer-${selectedDocument?.document_guid || 'none'}`}
                document={selectedDocument}
                authorizationId={authorizationId}
                onClose={() => onDocumentSelect(null)}
                conversationLinks={conversationLinks}
                showConversationSources={true}
                documents={documents}
                documentsLoading={documentsLoading}
                documentsError={documentsError}
                onDocumentSelect={onDocumentSelect}
                selectedDocuments={selectedDocuments}
                onSelectedDocumentsChange={onSelectedDocumentsChange}
                gradeData={gradeData}
                uiMode={uiMode}
                onUrlClick={(url) => {
                  // Handle URL clicks - could open in new tab or display in viewer
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          </>
        ) : (
          <div style={{ 
            padding: tokens.spacingVerticalL,
            textAlign: 'center',
            color: tokens.colorNeutralForeground2
          }}>
            <DocumentRegular style={{ fontSize: '32px', marginBottom: tokens.spacingVerticalM }} />
            <Text>Enter an authorization number to view documents</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentPanel;